import { Calendar, ExternalLink } from "lucide-react";
import { format, isToday, isTomorrow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCalendarEvents } from "@/hooks/useCalendar";
import { useIntegrations } from "@/hooks/useIntegrations";

function formatEventTime(start: string): string {
  const date = new Date(start);
  let dayLabel: string;
  if (isToday(date)) {
    dayLabel = "Today";
  } else if (isTomorrow(date)) {
    dayLabel = "Tomorrow";
  } else {
    dayLabel = format(date, "EEE");
  }
  return `${dayLabel} ${format(date, "h:mm a")}`;
}

export function CalendarWidget() {
  const { data: events, isLoading } = useCalendarEvents(7);
  const { data: integrations } = useIntegrations();

  const hasGmail = integrations?.some((i) => i.type === "gmail" && i.enabled);

  // Hide widget entirely if Gmail isn't connected
  if (!hasGmail) return null;

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Upcoming</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading events...</p>
        ) : !events || events.length === 0 ? (
          <p className="text-xs text-muted-foreground">No upcoming events</p>
        ) : (
          <ul className="space-y-2">
            {events.slice(0, 8).map((event) => (
              <li key={event.id} className="flex items-start gap-2 text-sm">
                <span className="text-xs text-muted-foreground whitespace-nowrap mt-0.5">
                  {formatEventTime(event.start)}
                </span>
                <span className="flex-1 truncate">{event.title}</span>
                {event.html_link && (
                  <a
                    href={event.html_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
